/**
 * /sites/:siteId/corpus — Corpus paste box + searchable list.
 *
 * Big textarea at the top; paste, hit save, the Worker auto-parses via
 * DeepSeek and we surface the parse below the original for review. ⌘K
 * search hits the FTS5 mirror.
 *
 * Per Ultra Plan v3 §step-25.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { api } from '../lib/api.ts'

interface CorpusItem {
  id: string
  source: string | null
  raw: string
  parsed: string | null
  themes: string | null
  upvotes: number | null
  fetched_at: number
  reviewed_by: string | null
}

export function CorpusScreen() {
  const { siteId = 'chc' } = useParams()
  const [items, setItems] = useState<CorpusItem[]>([])
  const [query, setQuery] = useState('')
  const [paste, setPaste] = useState('')
  const [source, setSource] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  async function refresh(q?: string) {
    setLoading(true)
    try {
      const res = await api<{ items: CorpusItem[] }>(
        `/api/sites/${siteId}/corpus${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      )
      setItems(res.items)
    } finally {
      setLoading(false)
    }
  }

  async function save() {
    if (paste.trim().length < 10) return
    setSaving(true)
    try {
      await api(`/api/sites/${siteId}/corpus`, {
        method: 'POST',
        body: JSON.stringify({ raw: paste, source: source || undefined }),
      })
      setPaste('')
      setSource('')
      await refresh(query || undefined)
    } catch (err) {
      alert(`Save failed: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Corpus</h1>
          <div className="subtitle">
            Paste research material — DeepSeek auto-parses source + themes when available.
          </div>
        </div>
        <input
          type="search"
          placeholder="Search corpus…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') refresh(query.trim() || undefined)
          }}
          style={{
            padding: '0.45rem 0.75rem',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius)',
            minWidth: 240,
          }}
        />
      </div>

      <section
        style={{
          background: 'var(--bg-elev)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
        }}
      >
        <textarea
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder="Paste research: a Reddit thread, PubMed abstract, blog quote, anything. Min 10 chars."
          style={{
            width: '100%',
            minHeight: '10rem',
            padding: '0.6rem 0.75rem',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: '0.85rem',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
          <input
            type="text"
            placeholder="Source hint (optional, e.g. 'reddit', 'pubmed')"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            style={{
              padding: '0.4rem 0.6rem',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              minWidth: 280,
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || paste.trim().length < 10}
            onClick={save}
          >
            {saving ? 'Parsing + saving…' : 'Save'}
          </button>
        </div>
      </section>

      <h2 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-faint)', margin: '0 0 0.5rem' }}>
        {query ? `${items.length} results` : `${items.length} items`}
      </h2>
      {loading && <div className="loading">Loading…</div>}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.75rem' }}>
        {items.map((item) => (
          <li key={item.id} style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '0.75rem 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-faint)' }}>
              <span>
                {item.source ? <strong style={{ color: 'var(--text-muted)' }}>{item.source}</strong> : '(no source)'}
                {item.themes && (() => {
                  try {
                    const t = JSON.parse(item.themes) as string[]
                    return t.length > 0 ? <> · {t.join(', ')}</> : null
                  } catch {
                    return null
                  }
                })()}
              </span>
              <span>{new Date(item.fetched_at * 1000).toLocaleDateString()}</span>
            </div>
            <div
              style={{
                marginTop: '0.4rem',
                fontSize: '0.85rem',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }}
            >
              {item.raw}
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}
